import { ApiResponse } from '@/types/api';

export interface ConfigStore {
  key: string;
  value: any;
  description?: string;
  isEncrypted?: boolean;
  isDisabled?: boolean;
  isPublic?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ConfigValue {
  value: any;
  description?: string;
  isEncrypted?: boolean;
  isPublic?: boolean;
}

export interface BatchConfigRequest {
  configs: Array<{
    key: string;
    value: any;
    description?: string;
    isEncrypted?: boolean;
    isPublic?: boolean;
  }>;
}

class ConfigService {
  private baseUrl: string;
  private headers: HeadersInit;

  constructor(token: string) {
    this.baseUrl = `${import.meta.env.VITE_CC_API_URL}/v1/config`;
    this.headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Update authorization token
   * @param token New auth token
   */
  updateToken(token: string) {
    this.headers = {
      ...this.headers,
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Get a specific configuration value
   * @param key Configuration key
   * @returns Promise with configuration value
   */
  async getConfig(key: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/${key}`, {
        method: 'GET',
        headers: this.headers
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching config:', error);
      throw error;
    }
  }

  /**
   * Get all configurations for the authenticated user
   * @returns Promise with array of configurations
   */
  async getAllConfigs(): Promise<ApiResponse<ConfigStore[]>> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers: this.headers
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching all configs:', error);
      throw error;
    }
  }

  /**
   * Set a configuration value
   * @param key Configuration key
   * @param config Configuration value and metadata
   * @returns Promise with updated configuration
   */
  async setConfig(key: string, config: ConfigValue): Promise<ApiResponse<ConfigStore>> {
    try {
      const response = await fetch(`${this.baseUrl}/${key}`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(config)
      });

      const data = await response.json();
      return data.data.value;
    } catch (error) {
      console.error('Error setting config:', error);
      throw error;
    }
  }

  /**
   * Batch set multiple configurations
   * @param request Batch configuration request
   * @returns Promise with array of updated configurations
   */
  async batchSetConfigs(request: BatchConfigRequest): Promise<ApiResponse<ConfigStore[]>> {
    try {
      const response = await fetch(`${this.baseUrl}/batch`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(request)
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error batch setting configs:', error);
      throw error;
    }
  }

  /**
   * Hard delete a configuration
   * @param key Configuration key to delete
   * @returns Promise with success message
   */
  async deleteConfig(key: string): Promise<ApiResponse<string>> {
    try {
      const response = await fetch(`${this.baseUrl}/${key}`, {
        method: 'DELETE',
        headers: this.headers
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting config:', error);
      throw error;
    }
  }

  /**
   * Soft delete (disable) a configuration
   * @param key Configuration key to disable
   * @returns Promise with success message
   */
  async disableConfig(key: string): Promise<ApiResponse<string>> {
    try {
      const response = await fetch(`${this.baseUrl}/${key}/disable`, {
        method: 'POST',
        headers: this.headers
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error disabling config:', error);
      throw error;
    }
  }
}

export const configService = new ConfigService('');
